import store from '../store'
import mqttClient from './aws-iot/mqtt'
import segment from '@/services/segment'
import { getClientConfig } from '@/services/clientConfig'
import { v4 as uuidV4 } from 'uuid'
import http from './webhook/httpconfig'
import { cancelHTTPRequests } from '@/services/utils'
import { generateHeaders } from './webhook/webhook'
import { cookieConstant, eventType, jsonConfigKeys } from './constants'
import {
  emitEventToParentWindow,
  updateConnectionStatus,
  validateHexCode,
  updateCalloutBubble,
  setBrowserAndDeviceDetailsInStore,
  removeCalloutBubbleContainer,
  renderCalloutBubble
} from './utils'
import session from './session'
import updateChatWidget from './updateChatWidget'
import CustomFont from './customFont'
import { getCookie, setCookie } from './cookie'
import { getBotJSONConfig } from './CDN_fetchConfig'
import { chatWidgetScreenSizes, constantStrings, status } from './constants'
import Storage from './storageLib'
import I18n from '@/services/localization/i18n-setup'
import { getUserIPAddress } from './userIPAddress'
import streamHandler from './streamHandler'
import AppLog from '@/services/errors/app-logs'
import {
  handleQuickReplyBubbleClick,
  handleTextBubbleClick
} from './calloutBubbleEventHandlers'
import { getFormEncryptionPublicKey } from '@/services/CDN_fetchConfig'
import { getBotPCIPolicy } from '@/services/bots'

export async function init() {
  setBrowserAndDeviceDetailsInStore()
  const botRefId = initBotRefId()
  const clientConfig = await initClientConfig()
  const isResponseAnalyzerEnabled = window.parent.document
    .getElementById('netomiChatWindow')
    .getAttribute('isResponseAnalyzerWidget')
  let jsonConfig
  if (isResponseAnalyzerEnabled) {
    jsonConfig = await responseAnalyzerInitConfig()
  } else {
    jsonConfig = await initJSONConfig(botRefId)
  }
  await initFeatureFlags()
  if (jsonConfig?.eventAnalyticsMP) {
    segment.loadSegmentScript(clientConfig?.cwSegmentTokenId)
  }
  if (jsonConfig?.isSensitiveDataMaskingEnabled) {
    const updateBotPCIPolicy = await getBotPCIPolicy()
    store.dispatch('updateBotPCIPolicy', updateBotPCIPolicy)
  }
  const formEncryptionPublicKey = await getFormEncryptionPublicKey()
  store.dispatch('updateFormEncryptionPublicKey', formEncryptionPublicKey)
  store.dispatch('updateAlerts')
  store.dispatch('updateTermsAndConditions')
  const userIPAddress = await getUserIPAddress() // get ip address for passing as custom parameters
  store.dispatch('updateUserIPAddress', userIPAddress)
  const currentLocale = Storage.getLocalStorageData(
    constantStrings.CURRENT_LOCALE
  )
  CustomFont.init(jsonConfig)
  updateChatWidget.init()
  updateCalloutBubble()
  await Promise.all([
    I18n.loadBotJsonConfigLocaleMessages(store.state.defaultLocale),
    I18n.loadBotConstantLocaleMessages(currentLocale),
    I18n.loadBotJsonConfigLocaleMessages(
      Storage.getLocalStorageData(constantStrings.CURRENT_LOCALE) ||
        store.state.defaultLocale
    )
  ])
  updateColorAndTitle(jsonConfig)
  initWidgetId()
  initUser()
  const isTrustedDomain = initWhitelistingURLs(jsonConfig)

  window.addEventListener('message', async (event) => {
    switch (event.data.eventType) {
      case 'HANDLE_TEXT_BUBBLE_CLICK':
        handleTextBubbleClick()
        removeCalloutBubbleContainer()
        break
      case 'HANDLE_QUICK_REPLY_BUBBLE_CLICK':
        handleQuickReplyBubbleClick(event?.data?.data?.index)
        removeCalloutBubbleContainer()
        break
      case 'HANDLE_CALLOUT_BUBBLE_CLOSE':
        if (
          store.state.widgetConfig?.calloutBubble?.dismissCalloutBubbleOnCross
        ) {
          Storage.setLocalStorageData(
            constantStrings.DISMISS_CB_ON_CROSS,
            getCookie(cookieConstant.SESSION.CONVERSATION_ID)
          )
        }
        removeCalloutBubbleContainer()
        break
      default:
        break
    }
  })
  renderCalloutBubble()

  if (isTrustedDomain) {
    if (store.getters.isKnowledgeAiEnabled)
      emitEventToParentWindow(eventType.NO_SCROLL_ALLOWED)
  } else {
    emitEventToParentWindow(
      eventType.UPDATE_PARENT_WINDOW_STYLE,
      chatWidgetScreenSizes.HIDE
    )
  }
  initSession(jsonConfig)
  streamHandler.init()
  Storage.setSessionStorageData('userInteracted', false)
  Storage.setSessionStorageData('tabId', uuidV4())
  Storage.setLocalStorageData(constantStrings.CONNECTION_STATUS_LABEL, {
    status: navigator.onLine ? status.ONLINE : status.OFFLINE,
    lastUpdatedTime: Date.now()
  })

  // Add Event listeners
  addEventListenerOnWindow()
}

const updateColorAndTitle = (jsonConfig) => {
  const color = window.parent.document
    .getElementById('netomiChatWindow')
    .getAttribute('themeColor')
  const title = window.parent.document
    .getElementById('netomiChatWindow')
    .getAttribute('botTitle')
  if (color && validateHexCode(color)) {
    jsonConfig.theme.color = color
  }
  if (title) {
    // eslint-disable-next-line no-prototype-builtins
    jsonConfig.botHeader && jsonConfig.botHeader.hasOwnProperty('title')
      ? (jsonConfig.botHeader.title = title)
      : (jsonConfig.title = title)
  }
  store.dispatch('updateWidgetConfig', jsonConfig)
}

const initWidgetId = () => {
  let widgetConfig = Storage.getLocalStorageData('widgetConfig') // getting widgetConfig from local storage
  let widgetId = widgetConfig?.widgetId
  store.dispatch('updateWidgetId', widgetId)
  return widgetId
}

const initBotRefId = () => {
  let botRefId = ''
  try {
    let widgetConfig = Storage.getLocalStorageData('widgetConfig')
    if (!widgetConfig)
      throw new Error('WidgetConfig not found, Storage access denied.')
    botRefId = widgetConfig?.botRefId
  } catch {
    botRefId = window.parent.document
      .getElementById('netomiChatWindow')
      .getAttribute('botRefId')
  } finally {
    store.dispatch('updateBotRefId', botRefId)
  }
  return botRefId
}

const initUser = () => {
  let userId = getCookie(cookieConstant.SESSION.USER_ID)
  if (!userId) {
    userId = generateUserId()
    setCookie(cookieConstant.SESSION.USER_ID, userId, {
      expires: 'Fri, 31 Dec 9999 23:59:59 GMT'
    })
  }
  store.dispatch('updateUserId', userId)
  return userId
}

const generateUserId = () => {
  return uuidV4()
}

const initJSONConfig = async (botRefId) => {
  const jsonConfig = await getBotJSONConfig(botRefId)
  store.dispatch('updateWidgetConfig', jsonConfig)
  if (jsonConfig?.dynamicSource) {
    new AppLog({
      name: 'dynamicSource',
      message: {
        dynamicSource: jsonConfig?.dynamicSource
      },
      logLevel: 'info',
      service: 'Init'
    })
  }
  return jsonConfig
}

const initFeatureFlags = async () => {
  try {
    const response = await http.request({
      method: 'get',
      url: `/api/feature-flags`,
      headers: {
        'Cache-Control': 'no-cache',
        ...generateHeaders()
      },
      cancelToken: cancelHTTPRequests()
    })
    const featureFlagsList = response.data?.payload || {}
    store.dispatch('updateFeatureFlagsList', featureFlagsList || {})
  } catch {
    store.dispatch('updateFeatureFlagsList', {})
  }
}

const responseAnalyzerInitConfig = async () => {
  const jsonConfig = await getBotJSONConfig(jsonConfigKeys.RESPONSE_ANALYZER)
  store.dispatch('updateWidgetConfig', jsonConfig)
  return jsonConfig
}

const initClientConfig = async () => {
  const configs = await getClientConfig()
  store.dispatch('setClientConfig', configs)
  return configs
}

const initWhitelistingURLs = (jsonConfig) => {
  if (!jsonConfig.whitelisting || !jsonConfig.whitelisting.enabled) {
    return true
  }
  let parentDomain = document.referrer || window.parent.location.origin
  parentDomain = new URL(parentDomain).origin
  const allowedDomains = jsonConfig.whitelisting.domains.map(
    (domain) => domain.name
  )
  let isAllowed = false
  allowedDomains.forEach((domain) => {
    if (domain.startsWith(parentDomain)) {
      isAllowed = true
    }
  })
  return isAllowed
}

const initSession = (jsonConfig) => {
  const conversationId =
    getCookie(cookieConstant.SESSION.CONVERSATION_ID) || null
  if (conversationId) {
    new AppLog({
      name: 'Init Session',
      message: `ConversationId inside the fn initSession: ${conversationId}, conversationId inside cookie: ${getCookie(cookieConstant.SESSION.CONVERSATION_ID)}`,
      logLevel: 'info',
      service: 'Init'
    })
    mqttClient.createSubscription(conversationId)
  }
  const createdAt = getCookie(cookieConstant.SESSION.CREATED_AT) || null
  session.init(conversationId, createdAt)
  return session.getConversationId(jsonConfig)
}

const addEventListenerOnWindow = () => {
  window.addEventListener('offline', updateConnectionStatus)
  window.addEventListener('online', updateConnectionStatus)
}
